import { faSpinner } from "@jewlr/pro-regular-svg-icons/faSpinner"
import PropTypes from "prop-types"
import { useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Link } from "react-router-dom"

import useCollection from "areas/collection/hoc/use-collection"
import {
  clearPendingAnnouncement,
  setPendingAnnouncement,
  updateNewPageStartIndex,
} from "areas/collection/store/actions"
import Button from "components/ui/button"
import FontAwesomeIcon from "components/ui/font-awesome-icon"

const getAnnouncementPrefix = (pendingAnnouncement) => {
  if (!pendingAnnouncement) return ""

  if (pendingAnnouncement === "filters_cleared") return "All filters cleared. "
  if (pendingAnnouncement === "load_more") return "More products loaded. "

  const separatorIndex = pendingAnnouncement.indexOf(":")
  if (separatorIndex === -1) return ""

  const action = pendingAnnouncement.slice(0, separatorIndex)
  const label = pendingAnnouncement.slice(separatorIndex + 1)

  switch (action) {
    case "filter_applied":
      return `Filter applied: ${label}. `
    case "filter_removed":
      return `Filter removed: ${label}. `
    case "sort":
      return `Sort changed to: ${label}. `
    default:
      return ""
  }
}

const getProductAnnouncement = (
  pendingAnnouncement,
  loadedProductCount,
  totalProductCount
) => {
  const productSuffix =
    totalProductCount === 0
      ? "No products found."
      : `Showing ${loadedProductCount} of ${totalProductCount} product${totalProductCount === 1 ? "" : "s"}.`

  return `${getAnnouncementPrefix(pendingAnnouncement)}${productSuffix}`
}

const LoadMoreProducts = ({ newPageFocusRef }) => {
  const dispatch = useDispatch()
  const pendingPageStart = useRef(null)
  const {
    data = {},
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isFetchNextPageError,
    isFetching,
  } = useCollection()
  const currentNumOfProducts = data.product_result?.length ?? -1
  const { next_page, total_product_count } = data.pagination ?? {}
  const loadedProductCount = (data.product_result || []).filter(
    (item) => item.item_type !== "ad_block"
  ).length

  useEffect(() => {
    if (isFetchNextPageError) pendingPageStart.current = null
    if (
      !isFetchingNextPage &&
      pendingPageStart.current !== null &&
      currentNumOfProducts > pendingPageStart.current
    ) {
      newPageFocusRef.current?.focus()
      pendingPageStart.current = null
    }
  }, [
    isFetchingNextPage,
    isFetchNextPageError,
    currentNumOfProducts,
    newPageFocusRef,
  ])

  const pendingAnnouncement = useSelector(
    (state) => state.collection.pendingAnnouncement
  )
  const wasFetchingRef = useRef(false)
  const [announcement, setAnnouncement] = useState("")

  useEffect(() => {
    if (
      !isFetching &&
      wasFetchingRef.current &&
      typeof total_product_count === "number"
    ) {
      setAnnouncement(
        getProductAnnouncement(
          pendingAnnouncement,
          loadedProductCount,
          total_product_count
        )
      )
      dispatch(clearPendingAnnouncement())
    }

    wasFetchingRef.current = isFetching
  }, [
    dispatch,
    isFetching,
    loadedProductCount,
    pendingAnnouncement,
    total_product_count,
  ])

  return (
    <div className="flex flex-col items-center justify-center gap-20 px-20 py-8">
      <p
        aria-atomic="true"
        aria-busy={isFetching}
        aria-live="polite"
        className="text-grey-65 text-14 leading-16"
        role="status"
      >
        {typeof total_product_count === "number" &&
          `Viewing ${loadedProductCount} of ${total_product_count} products`}
      </p>
      <p
        aria-atomic="true"
        aria-live="polite"
        className="sr-only"
        role="status"
      >
        {announcement}
      </p>
      {hasNextPage && (
        <Button
          aria-disabled={isFetchingNextPage}
          aria-label={
            isFetchingNextPage ? "Loading more products" : "View more products"
          }
          asChild
          className="w-260"
          data-cy="collection-load-product-button"
          loading={isFetchingNextPage}
          variant="secondary"
        >
          <Link
            onClick={(e) => {
              e.preventDefault()
              if (!isFetchingNextPage) {
                pendingPageStart.current = currentNumOfProducts
                dispatch(updateNewPageStartIndex(currentNumOfProducts))
                dispatch(setPendingAnnouncement("load_more"))
                fetchNextPage()
              }
            }}
            replace
            to={`/${next_page}`}
          >
            {isFetchingNextPage ? "Loading" : "View More"}
            {isFetchingNextPage && (
              <FontAwesomeIcon className="ml-4" icon={faSpinner} spin />
            )}
          </Link>
        </Button>
      )}
    </div>
  )
}

LoadMoreProducts.propTypes = {
  newPageFocusRef: PropTypes.object,
}

export default LoadMoreProducts
